import React, { useEffect, useState, useRef } from "react";
import {
  TextField,
  Button,
  Container,
  Grid,
  LinearProgress,
  CircularProgress,
  MenuItem,
  Select,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Message from "./Message";
import { MessageDto } from "./MessageDto";
import SendIcon from "@mui/icons-material/Send";
import { utils, writeFile } from "xlsx";
import { useHistory } from "react-router-dom";
import { sendChatMessage } from "../../actions"; // adjust path if needed
import apiBase from "../../utils/apiBase";

const getAuthHeaders = () => ({
  "auth-token": localStorage.getItem("auth"),
  "user-id": localStorage.getItem("userId"),
  database: localStorage.getItem("db"),
});

const Chat = (props) => {
  const [isWaiting, setIsWaiting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [assistant] = useState(null);
  const [thread, setThread] = useState(null);
  const [assistantList, setAssistantList] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState("");
  const [selectedFormat, setSelectedFormat] = useState("txt");
  const messagesEndRef = useRef(null);
  const selectedDatabase = props.user.mainDatabase;
  const isDatabaseValid = Boolean(selectedDatabase);
  const history = useHistory();

  // Redirect to Home if the database is not valid
  useEffect(() => {
    if (!isDatabaseValid) {
      history.push("/home"); // Redirect to Home if the database is not valid
    }
  }, [isDatabaseValid, history]);

  useEffect(() => {
    if (assistantList.length > 0) {
      setSelectedAssistant(assistantList[0].id);
    }
  }, [assistantList]);

  useEffect(() => {
    const loadAssistants = async () => {
      try {
        const res = await fetch(
          `${apiBase()}/api/chat/assistants?database=${selectedDatabase}`,
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          }
        );

        const data = await res.json();

        const safeAssistants = Array.isArray(data.assistants)
          ? data.assistants
          : [];

        setAssistantList(safeAssistants);

        if (safeAssistants.length > 0) {
          setSelectedAssistant(safeAssistants[0].id);
        }
      } catch (e) {
        toast.error("Failed to load assistants");
      }
    };

    loadAssistants();
  }, [selectedDatabase]);

  useEffect(() => {
    if (assistant) {
      logMessage(
        `Assistant: Hi, I'm your personal ${assistant.name}. How can I help you?`
      );
      setMessages([
        {
          content: `Hi, I'm your personal ${assistant.name}. How can I help you?`,
          isUser: false,
        },
      ]);
    }
  }, [assistant]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const createNewMessage = (content, isUser) => {
    const newMessage = new MessageDto(isUser, content);
    return newMessage;
  };

  const logMessage = (_content) => {};

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    if (!selectedAssistant) {
      toast.error("Assistant not selected yet");
      return;
    }

    const userText = input;

    setMessages((m) => [...m, createNewMessage(userText, true)]);
    setInput("");
    setIsWaiting(true);

    try {
      const data = await sendChatMessage({
        message: userText,
        assistantId: selectedAssistant,
        threadId: thread?.id || undefined, // 👈 FIX #1
        database: selectedDatabase,
      });

      if (data.threadId) {
        // 👈 FIX #2
        setThread({ id: data.threadId });
      }

      setMessages((m) => [...m, createNewMessage(data.reply, false)]);
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Chat error");
    }

    setIsWaiting(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  const handleAssistantChange = (event) => {
    setSelectedAssistant(event.target.value);
  };

  const handleFormatChange = (event) => {
    setSelectedFormat(event.target.value);
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const downloadChatHistory = () => {
    const formattedDate = new Date()
      .toISOString()
      .slice(0, 10)
      .replace(/-/g, "");
    const extension = selectedFormat === "txt" ? "txt" : "xlsx";
    const fileName = `${formattedDate}_chat_history.${extension}`;

    if (selectedFormat === "txt") {
      // Download as text file
      downloadAsText(fileName);
    } else if (selectedFormat === "excel") {
      // Download as Excel file
      downloadAsExcel(fileName);
    }
  };

  const downloadAsText = (fileName) => {
    // Format messages as text
    const formattedMessages = messages
      .map((message) => {
        return `${message.isUser ? "User" : "Assistant"}: ${message.content}`;
      })
      .join("\n");

    // Create a Blob with the formatted text
    const file = new Blob([formattedMessages], { type: "text/plain" });

    // Trigger download
    triggerDownload(file, fileName);
  };

  const downloadAsExcel = (fileName) => {
    // Prepare data for Excel
    const data = messages.map((message, index) => {
      // Extract links from the message content
      const links = extractLinks(message.content);
      // Remove links from the message content
      const contentWithoutLinks = removeLinks(message.content);
      return [
        index + 1,
        message.isUser ? "User" : "Assistant",
        contentWithoutLinks,
        links.join(", "),
      ];
    });

    // Create a new Excel workbook
    const wb = utils.book_new();
    const ws = utils.aoa_to_sheet([
      ["Message No.", "Sender", "Content", "Links"],
      ...data,
    ]);
    utils.book_append_sheet(wb, ws, "Chat History");

    // Save the workbook as a file
    writeFile(wb, fileName);
  };

  // Function to extract links from a string
  const extractLinks = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.match(urlRegex) || [];
  };

  // Function to remove links from a string
  const removeLinks = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(urlRegex, "").trim();
  };

  const triggerDownload = (file, fileName) => {
    // Create a temporary link element
    const element = document.createElement("a");

    // Set the href attribute to the Blob URL
    element.href = URL.createObjectURL(file);

    // Set the download attribute to the file name
    element.download = fileName;

    // Append the element to the document body
    document.body.appendChild(element);

    // Trigger the click event to initiate download
    element.click();

    // Remove the element from the document body
    document.body.removeChild(element);
  };

  return (
    <Container>
      <Grid container direction="column" spacing={2}>
        {(messages || []).map((message, index) => (
          <Grid
            item
            key={index}
            alignSelf={message.isUser ? "flex-end" : "flex-start"}
          >
            <Message key={index} message={message} />
          </Grid>
        ))}
        <div ref={messagesEndRef} />
      </Grid>
      <Grid container direction="row" spacing={2} alignItems="center">
        <Grid item xs={9}>
          <TextField
            label="Type your message"
            variant="outlined"
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={isWaiting || !selectedAssistant}
          />
          {isWaiting && <LinearProgress color="inherit" />}
        </Grid>
        <Grid item xs={1}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSendMessage}
            disabled={isWaiting || !selectedAssistant}
          >
            {isWaiting ? <CircularProgress color="inherit" /> : <SendIcon />}
          </Button>
        </Grid>
        <Grid item xs={2}>
          <Select
            value={selectedAssistant}
            onChange={handleAssistantChange}
            fullWidth
            disabled={isWaiting}
          >
            {(assistantList || []).map((assistant) => (
              <MenuItem key={assistant.id} value={assistant.id}>
                {assistant.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12}>
          <Select
            value={selectedFormat}
            onChange={handleFormatChange}
            disabled={messages.length === 0}
          >
            <MenuItem value="txt">Text (TXT)</MenuItem>
            <MenuItem value="excel">Excel (XLSX)</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={downloadChatHistory}
            disabled={messages.length === 0}
          >
            Download Chat History
          </Button>
        </Grid>
      </Grid>
      <ToastContainer />
    </Container>
  );
};

export default Chat;
